import logger from '@/util/logger';
import { isStaleChunkError } from '@/util/staleChunkError';

const log = logger('staleChunkReload');

const RELOAD_TS_KEY = 'rock-admin:stale-chunk-reload-ts';
const RELOAD_DEBOUNCE_MS = 10_000;

// A stale page fails many imports at once (every icon on a list page), so the first failure in a
// page-load makes the decision and the rest are the same event, not new ones.
let reloadRequested = false;
let missingChunkReported = false;

/**
 * Self-heal stale code-split chunks after a deploy.
 *
 * Vite content-hashes every lazily-imported chunk (each `lucide-react/dynamic` icon is its own
 * `assets/<name>-<hash>.js`). A tab opened before a deploy still references the old hashes; once
 * Firebase Hosting replaces those files, importing one fails with "Failed to fetch dynamically
 * imported module". We reload once to pull the fresh `index.html` + chunk graph.
 *
 * A sessionStorage timestamp debounces the reload: if the same import is STILL unfetchable right
 * after a reload, the chunk is genuinely gone (a broken deploy, not a stale tab) so we stop instead
 * of looping. The debounce — rather than a one-shot flag — also lets a long-lived admin tab recover
 * from a *second* deploy later in the same session.
 *
 * The listener must NOT `preventDefault()` the Vite event: that makes the failed `import()` resolve
 * with `undefined` instead of rejecting, and the caller then crashes on the missing module (lucide's
 * DynamicIcon reads `.__iconData` off it). Left to reject, the failure stays in the caller's own
 * error path, and `datadog.ts` drops those reports because the reload is the recovery.
 */
export function installStaleChunkReload(): void {
    // Vite's first-class signal: a modulepreload for a dynamic import failed to load.
    window.addEventListener('vite:preloadError', (event) => {
        reloadForStaleChunk(event.payload);
    });

    // Belt-and-suspenders: a bare `import()` that rejects doesn't always emit vite:preloadError.
    window.addEventListener('unhandledrejection', (event) => {
        if (isStaleChunkError(toErrorMessage(event.reason))) {
            reloadForStaleChunk(event.reason);
        }
    });
}

function reloadForStaleChunk(cause: unknown): void {
    if (reloadRequested) return;

    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_TS_KEY) ?? '0');
    if (Date.now() - lastReloadAt < RELOAD_DEBOUNCE_MS) {
        if (missingChunkReported) return;
        missingChunkReported = true;
        // We reloaded moments ago and the chunk is still unfetchable — it's genuinely missing, not
        // just stale. Stop, so we don't loop the tab. This is the actionable "broken deploy" signal,
        // so keep the cause even when it arrives as a bare string (no Error to carry the message).
        log.error('Dynamic import still failing after reload — chunk is genuinely missing', {
            err: cause instanceof Error ? cause : undefined,
            reason: toErrorMessage(cause),
        });
        return;
    }

    reloadRequested = true;
    sessionStorage.setItem(RELOAD_TS_KEY, `${Date.now()}`);
    log.warn('Reloading to recover from a stale code-split chunk (likely a deploy mid-session)');
    window.location.reload();
}

function toErrorMessage(reason: unknown): string {
    if (reason instanceof Error) return reason.message;
    return typeof reason === 'string' ? reason : '';
}
